import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Xmark } from '@gravity-ui/icons';
import { getUsers } from '../../services/auth';
import { TIMEZONE_OPTIONS } from '../../constants/timezones';
import { TimeInput } from '../DateTimeInput';
import DecodeOptimizationPanel from '../DecodeOptimizationPanel/DecodeOptimizationPanel';
import EntityImagePicker from '../EntityImagePicker/EntityImagePicker';
import { levelMap } from '../../constants/adminPageConstants';
import ZoneEditor from '../../pages/ViolationsPage/analytics/ZoneEditor';
import DetectionProfileSection from '../../pages/ViolationsPage/analytics/DetectionProfileSection';
import '../../pages/ViolationsPage/ViolationsPage.css';

const getOwnerId = (owner) => (typeof owner === 'object' ? owner.id : owner);

const getOwnerLabel = (owner) => {
  if (typeof owner !== 'object') return owner;
  return owner.email ? owner.email.split('@')[0] : owner.username || owner.id;
};

const normalizeOwnersResponse = (response) => {
  if (Array.isArray(response)) return response;
  return response?.data || response?.items || [];
};

const OwnerSearchSelect = ({ owners = [], value, errors, onChange }) => {
  const [items, setItems] = useState(owners || []);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    setItems(owners || []);
  }, [owners]);

  useEffect(() => {
    if (!value) {
      setSelectedLabel('');
      return;
    }

    const selected = items.find((owner) => getOwnerId(owner) === value) ||
      owners.find((owner) => getOwnerId(owner) === value);
    if (selected) setSelectedLabel(getOwnerLabel(selected));
  }, [items, owners, value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        if (selectedLabel) setQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedLabel]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setLoading(true);
      getUsers(query ? { email_like: query } : {})
        .then((response) => setItems(normalizeOwnersResponse(response)))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, query ? 300 : 0);

    return () => clearTimeout(timeoutId);
  }, [query]);

  const emitOwnerChange = (ownerId) => {
    onChange({ target: { name: 'owner', value: ownerId } });
  };

  const handleInputChange = (event) => {
    setSelectedLabel('');
    setQuery(event.target.value);
    setIsOpen(true);
    if (value) emitOwnerChange('');
  };

  const handleSelect = (owner) => {
    const ownerId = getOwnerId(owner);
    setSelectedLabel(getOwnerLabel(owner));
    setQuery('');
    setIsOpen(false);
    emitOwnerChange(ownerId);
  };

  const handleClear = () => {
    setSelectedLabel('');
    setQuery('');
    setIsOpen(false);
    emitOwnerChange('');
  };

  const inputValue = selectedLabel || query;

  return (
    <div className="mb-3 owner-search-select" ref={wrapperRef}>
      <label htmlFor="owner" className="form-label">Владелец *</label>
      <div className="owner-search-input-wrap">
        <input
          type="text"
          className={`form-control ${errors.owner ? 'is-invalid' : ''}`}
          id="owner"
          name="owner"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          autoComplete="off"
          placeholder="Выберите владельца"
          required
        />
        {inputValue && (
          <button
            type="button"
            className="owner-search-clear"
            onClick={handleClear}
            aria-label="Очистить владельца"
          >
            <Xmark width={16} height={16} />
          </button>
        )}
      </div>
      {isOpen && (
        <div className="owner-search-dropdown">
          {loading && <div className="owner-search-option muted">Загрузка…</div>}
          {!loading && items.length === 0 && (
            <div className="owner-search-option muted">Ничего не найдено</div>
          )}
          {!loading && items.map((owner) => (
            <button
              type="button"
              key={getOwnerId(owner)}
              className="owner-search-option"
              onClick={() => handleSelect(owner)}
            >
              {getOwnerLabel(owner)}
            </button>
          ))}
        </div>
      )}
      {errors.owner && <div className="invalid-feedback d-block">{errors.owner}</div>}
    </div>
  );
};

const AdminModalForm = ({
  modal,
  levelLabels,
  availableOwners,
  isPeopleLikeCameraType,
  onClose,
  onSubmit,
  onFormChange,
  onTypeChange,
  onShiftChange,
  onAddShift,
  onRemoveShift,
  onResumeArchive,
  onImageFileChange,
  onImageRemoveChange,
  onRetryImage,
}) => {
  const { mode, level, data, errors, loading } = modal;
  const isEdit = mode === 'edit';
  const isCamera = level === 'cameras';
  const liveArchiveUrlWarning = Boolean(
    data?.live_rtsp_url?.trim() &&
    data?.archive_rtsp_url?.trim() &&
    data.live_rtsp_url.trim() !== data.archive_rtsp_url.trim()
  );

  const archiveStatusText = {
    active: 'Архив активен',
    recording: 'Архив активен',
    disabled: 'Архив выключен',
    stopped: 'Архив выключен',
    system_stopped: 'Архив остановлен системой',
    error: 'Ошибка архива',
  }[data?.archive_status] || data?.archive_status || 'Архив выключен';

  return (
    <Modal show={modal.isOpen} onHide={onClose} size="xl" className="admin-modal" aria-labelledby="admin-modal-title">
      <div className="modal-header">
        <h5 className="modal-title" id="admin-modal-title">
          {isEdit ? 'Редактировать' : 'Создать'} {levelLabels[level]}
        </h5>
        <button type="button" className="btn-close" onClick={onClose} title="Закрыть" aria-label="Закрыть" />
      </div>

      <form onSubmit={onSubmit}>
        <div className="modal-body">
          <div className="row">
            <div className={isCamera ? 'col-md-6' : 'col-12'}>
              <div className="mb-3">
                {isCamera && data?.camera_id && (
                  <div className="camera-id-label">Камера №{data.camera_id}</div>
                )}
                <label htmlFor="name" className="form-label">Название *</label>
                <input
                  type="text"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  id="name"
                  name="name"
                  value={data?.name || ''}
                  onChange={onFormChange}
                  required
                />
                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>

              {!isCamera ? (
                <>
                  {/* ТЗ V5 №12: файл с компьютера вместо ручного URL. */}
                  <EntityImagePicker
                    entityType={levelMap[level]}
                    entityId={modal.originalItem?.id ?? null}
                    currentImage={data?.image_url || ''}
                    file={data?.image_file || null}
                    removeRequested={Boolean(data?.image_remove)}
                    onFileChange={onImageFileChange}
                    onRemoveChange={onImageRemoveChange}
                    disabled={loading}
                  />
                  {errors.image && (
                    <div className="admin-modal-image-error" role="alert">
                      <p className="vio-field-error">{errors.image}</p>
                      {onRetryImage && (
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={onRetryImage} disabled={loading}>
                          Повторить загрузку изображения
                        </button>
                      )}
                    </div>
                  )}
                  <OwnerSearchSelect
                    owners={availableOwners}
                    value={data?.owner}
                    errors={errors}
                    onChange={onFormChange}
                  />
                </>
              ) : (
                <>
                  <div className="mb-3">
                    <label htmlFor="rtsp_url" className="form-label">RTSP URL *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.rtsp_url ? 'is-invalid' : ''}`}
                      id="rtsp_url"
                      name="rtsp_url"
                      value={data?.rtsp_url || ''}
                      onChange={onFormChange}
                      placeholder="rtsp://"
                      required={mode === 'create'}
                    />
                    {errors.rtsp_url && <div className="invalid-feedback">{errors.rtsp_url}</div>}
                  </div>

                  <div className="mb-3">
                    <label htmlFor="type" className="form-label">Тип камеры *</label>
                    <select
                      className={`form-select ${errors.type ? 'is-invalid' : ''}`}
                      id="type"
                      name="type"
                      value={data?.type || ''}
                      onChange={onTypeChange}
                      required
                    >
                      <option value="">Выберите тип</option>
                      <option value="EQUIPMENT">Оборудование</option>
                      <option value="PEOPLE">Люди</option>
                      <option value="PPE">PPE</option>
                    </select>
                    {errors.type && <div className="invalid-feedback">{errors.type}</div>}
                  </div>

                  {isPeopleLikeCameraType(data?.type) && (
                    <div className="mb-3">
                      {(data.shifts || []).map((shift, index) => (
                        <div key={index} className="row mb-2 align-items-center">
                          <div className="col-md-5">
                            <TimeInput
                              className="form-control"
                              value={shift.start || ''}
                              onChange={(next) => onShiftChange(index, 'start', next)}
                              ariaLabel={`Начало смены ${index + 1}`}
                            />
                          </div>
                          <div className="col-md-5">
                            <TimeInput
                              className="form-control"
                              value={shift.end || ''}
                              onChange={(next) => onShiftChange(index, 'end', next)}
                              ariaLabel={`Конец смены ${index + 1}`}
                            />
                          </div>
                          <div className="col-md-2">
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => onRemoveShift(index)}
                            >
                              Удалить
                            </button>
                          </div>
                          {errors[`shift_${index}`] && (
                            <div className="text-danger small mt-1">{errors[`shift_${index}`]}</div>
                          )}
                        </div>
                      ))}

                      {(!data.shifts || data.shifts.length < 3) && (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={onAddShift}>
                          + Добавить смену
                        </button>
                      )}

                      {errors.shifts && <div className="text-danger small mt-1">{errors.shifts}</div>}
                    </div>
                  )}

                  <OwnerSearchSelect
                    owners={availableOwners}
                    value={data?.owner}
                    errors={errors}
                    onChange={onFormChange}
                  />

                  <div className="camera-live-archive-block">
                    <div className="form-check form-switch mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="live_enabled"
                        name="live_enabled"
                        checked={Boolean(data?.live_enabled)}
                        onChange={onFormChange}
                      />
                      <label className="form-check-label" htmlFor="live_enabled">
                        Включить live
                      </label>
                    </div>

                    {data?.live_enabled && (
                      <div className="camera-live-archive-fields">
                        <div className="mb-3">
                          <label htmlFor="live_rtsp_url" className="form-label">URL для live *</label>
                          <input
                            type="text"
                            className={`form-control ${errors.live_rtsp_url ? 'is-invalid' : ''}`}
                            id="live_rtsp_url"
                            name="live_rtsp_url"
                            value={data?.live_rtsp_url || ''}
                            onChange={onFormChange}
                            placeholder="rtsp://"
                          />
                          {errors.live_rtsp_url && <div className="invalid-feedback">{errors.live_rtsp_url}</div>}
                        </div>

                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label htmlFor="live_max_readers" className="form-label">Количество зрителей</label>
                            <select
                              className="form-select"
                              id="live_max_readers"
                              name="live_max_readers"
                              value={data?.live_max_readers ?? 2}
                              onChange={onFormChange}
                            >
                              <option value="0">0 - без ограничений</option>
                              <option value="1">1</option>
                              <option value="2">2</option>
                              <option value="3">3</option>
                              <option value="4">4</option>
                              <option value="5">5</option>
                              <option value="6">6</option>
                              <option value="7">7</option>
                              <option value="8">8</option>
                              <option value="9">9</option>
                              <option value="10">10</option>
                            </select>
                          </div>

                          <div className="col-md-6 mb-3">
                            <label htmlFor="live_rtsp_transport" className="form-label">live_rtsp_transport</label>
                            <select
                              className="form-select"
                              id="live_rtsp_transport"
                              name="live_rtsp_transport"
                              value={data?.live_rtsp_transport || 'tcp'}
                              onChange={onFormChange}
                            >
                              <option value="tcp">tcp</option>
                              <option value="udp">udp</option>
                              <option value="automatic">automatic</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="camera-live-archive-block">
                    <div className="form-check form-switch mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="archive_enabled"
                        name="archive_enabled"
                        checked={Boolean(data?.archive_enabled)}
                        onChange={onFormChange}
                      />
                      <label className="form-check-label" htmlFor="archive_enabled">
                        Включить Архив
                      </label>
                    </div>

                    {data?.archive_enabled && (
                      <div className="camera-live-archive-fields">
                        <div className={`archive-status-box archive-status-${data?.archive_status || 'disabled'}`}>
                          <strong>{archiveStatusText}</strong>
                          {data?.archive_status === 'system_stopped' && data?.archive_pause_reason && (
                            <div>{data.archive_pause_reason}</div>
                          )}
                          {data?.archive_status === 'system_stopped' && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm mt-2"
                              onClick={onResumeArchive}
                              disabled={loading || !data?.can_resume_archive}
                            >
                              Возобновить запись архива
                            </button>
                          )}
                        </div>

                        <div className="mb-3">
                          <label htmlFor="archive_rtsp_url" className="form-label">URL для Архива *</label>
                          <input
                            type="text"
                            className={`form-control ${errors.archive_rtsp_url ? 'is-invalid' : ''}`}
                            id="archive_rtsp_url"
                            name="archive_rtsp_url"
                            value={data?.archive_rtsp_url || ''}
                            onChange={onFormChange}
                            placeholder="rtsp://"
                          />
                          {errors.archive_rtsp_url && <div className="invalid-feedback">{errors.archive_rtsp_url}</div>}
                        </div>

                        {liveArchiveUrlWarning && (
                          <div className="alert alert-warning py-2">
                            URL live и архива отличаются, это может вызвать несколько одновременных подключений
                          </div>
                        )}

                        <div className="row">
                          <div className="col-md-4 mb-3">
                            <label htmlFor="archive_record_format" className="form-label">Формат записи</label>
                            <select
                              className="form-select"
                              id="archive_record_format"
                              name="archive_record_format"
                              value={data?.archive_record_format || 'fmp4'}
                              onChange={onFormChange}
                            >
                              <option value="fmp4">fMP4</option>
                              <option value="mpegts">MPEG-TS</option>
                            </select>
                          </div>

                          <div className="col-md-4 mb-3">
                            <label htmlFor="archive_retention_days" className="form-label">Срок хранения, дн. *</label>
                            <input
                              type="number"
                              className={`form-control ${errors.archive_retention_days ? 'is-invalid' : ''}`}
                              id="archive_retention_days"
                              name="archive_retention_days"
                              value={data?.archive_retention_days || ''}
                              onChange={onFormChange}
                              min="1"
                            />
                            {errors.archive_retention_days && (
                              <div className="invalid-feedback">{errors.archive_retention_days}</div>
                            )}
                          </div>

                          <div className="col-md-4 mb-3">
                            <label htmlFor="hot_buffer_retention_hours" className="form-label">Срок хранения в буфере, ч. *</label>
                            <input
                              type="number"
                              className={`form-control ${errors.hot_buffer_retention_hours ? 'is-invalid' : ''}`}
                              id="hot_buffer_retention_hours"
                              name="hot_buffer_retention_hours"
                              value={data?.hot_buffer_retention_hours || ''}
                              onChange={onFormChange}
                              min="1"
                            />
                            {errors.hot_buffer_retention_hours && (
                              <div className="invalid-feedback">{errors.hot_buffer_retention_hours}</div>
                            )}
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-4 mb-3">
                            <label htmlFor="archive_segment_duration_minutes" className="form-label">Длит. записи, мин. *</label>
                            <input
                              type="number"
                              className={`form-control ${errors.archive_segment_duration_minutes ? 'is-invalid' : ''}`}
                              id="archive_segment_duration_minutes"
                              name="archive_segment_duration_minutes"
                              value={data?.archive_segment_duration_minutes || ''}
                              onChange={onFormChange}
                              min="1"
                            />
                            {errors.archive_segment_duration_minutes && (
                              <div className="invalid-feedback">{errors.archive_segment_duration_minutes}</div>
                            )}
                          </div>

                          <div className="col-md-4 mb-3">
                            <label htmlFor="archive_priority" className="form-label">Приоритет архива</label>
                            <select
                              className="form-select"
                              id="archive_priority"
                              name="archive_priority"
                              value={data?.archive_priority || 'normal'}
                              onChange={onFormChange}
                            >
                              <option value="low">low</option>
                              <option value="normal">normal</option>
                              <option value="high">high</option>
                              <option value="critical">critical</option>
                            </select>
                          </div>

                          {/* ТЗ №2 §10.1: отдельного editable timezone архива больше нет —
                              архив использует общий «Часовой пояс камеры» (display_timezone). */}
                          <div className="col-md-4 mb-3">
                            <span className="form-label d-block">Часовой пояс</span>
                            <div className="form-control-plaintext" data-testid="archive-timezone-readonly">
                              {data?.display_timezone || 'Europe/Moscow'}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="frame_interval_sec" className="form-label">
                        Интервал кадров (сек) *
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errors.frame_interval_sec ? 'is-invalid' : ''}`}
                        id="frame_interval_sec"
                        name="frame_interval_sec"
                        value={data?.frame_interval_sec || ''}
                        onChange={onFormChange}
                        min="1"
                        required
                      />
                      {errors.frame_interval_sec && (
                        <div className="invalid-feedback">{errors.frame_interval_sec}</div>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="retention_minutes" className="form-label">
                        Время хранения (мин) *
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errors.retention_minutes ? 'is-invalid' : ''}`}
                        id="retention_minutes"
                        name="retention_minutes"
                        value={data?.retention_minutes || ''}
                        onChange={onFormChange}
                        min="1"
                        required
                      />
                      {errors.retention_minutes && (
                        <div className="invalid-feedback">{errors.retention_minutes}</div>
                      )}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="slow_retry_interval_minutes" className="form-label">
                        Интервал повторного подключения (мин) *
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errors.slow_retry_interval_minutes ? 'is-invalid' : ''}`}
                        id="slow_retry_interval_minutes"
                        name="slow_retry_interval_minutes"
                        value={data?.slow_retry_interval_minutes ?? 15}
                        onChange={onFormChange}
                        min="1"
                        max="1440"
                        step="1"
                      />
                      {errors.slow_retry_interval_minutes && (
                        <div className="invalid-feedback">{errors.slow_retry_interval_minutes}</div>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="display_timezone" className="form-label">Часовой пояс камеры</label>
                      <select
                        className="form-select"
                        id="display_timezone"
                        name="display_timezone"
                        value={data?.display_timezone || 'Europe/Moscow'}
                        onChange={onFormChange}
                      >
                        {TIMEZONE_OPTIONS.map((timezone) => (
                          <option key={timezone.value} value={timezone.value}>{timezone.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {isCamera && isEdit && data?.camera_id && (
                    <DecodeOptimizationPanel
                      cameraId={data.camera_id}
                      currentFrameInterval={Number(data.frame_interval_sec)}
                      displayTimezone={data.display_timezone || 'Europe/Moscow'}
                    />
                  )}
                  {isCamera && !isEdit && (
                    <div className="alert alert-secondary py-2">
                      Оптимизация декодирования доступна после создания камеры.
                      Новая камера использует безопасный режим ALL по умолчанию.
                    </div>
                  )}
                </>
              )}
            </div>

            {isCamera && (
              <div className="col-md-6">
                {isEdit && data?.camera_id ? (
                  <>
                    <div className="admin-zones-block">
                      <ZoneEditor cameraId={data.camera_id} capability="direct" />
                    </div>
                    <div className="admin-detection-block">
                      <DetectionProfileSection cameraId={data.camera_id} />
                    </div>
                  </>
                ) : (
                  <div className="alert alert-secondary py-2">
                    Зоны и детекции доступны после создания камеры.
                  </div>
                )}

                <div className="mb-3">
                  <label htmlFor="detection_configs" className="form-label">
                    Конфигурация детекции (JSON)
                  </label>
                  <textarea
                    className={`form-control ${errors.detection_configs ? 'is-invalid' : ''}`}
                    id="detection_configs"
                    name="detection_configs"
                    value={data?.detection_configs || ''}
                    onChange={onFormChange}
                    rows="6"
                    style={{ fontFamily: 'monospace', fontSize: '12px' }}
                  />
                  {errors.detection_configs && (
                    <div className="invalid-feedback">{errors.detection_configs}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          {errors.form && <p className="vio-field-error admin-modal-form-error">{errors.form}</p>}
          <button type="button" className="btn btn-secondary" onClick={onClose}>Отмена</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Сохранение…' : isEdit ? 'Сохранить' : 'Создать'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AdminModalForm;
